import { PayloadAction } from '@reduxjs/toolkit'
import { FIlterTypeVehicles, IActionVehicleRequest, VehiclesTypes } from './types'
import { all, apply, put, race, take, takeLatest } from 'redux-saga/effects'
import { VehiclesActions } from './index'
import vehicleService from '../../services/vehicles.service'
import { SnackbarActions, SnackBarMessageType } from '../snackbar'
import Messags from '../../assets/locales/pt/translation.json'

/**
 * <h5>Generator functions that make calls to the authentication service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef VehiclesSagas
 * @namespace VehiclesSagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof VehiclesSagas
 * @alias VehiclesSagas.request
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionVehicleRequest>} action User triggered action data.
 */
export function* request(action: PayloadAction<IActionVehicleRequest>) {
    const { vehiclesSuccess, vehiclesFailure } = VehiclesActions
    const { openSnackBar } = SnackbarActions

    try {
        const { filters } = action.payload

        /*
         * Validação da placa
         */
        if (filters?.plate) {
            const plate = filters.plate.toUpperCase()

            const validPlate =
                /^[A-Z]{3}[0-9][A-Z0-9]{3}$/.test(plate)

            if (!validPlate) {
                yield put(vehiclesFailure())

                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messags.REQUEST.VEHICLES.REQUEST.PLATE_INVALID}`
                }))

                return
            }
        }

        /*
         * Validação do RENAVAM
         */
        if (filters?.renavam) {
            if (!/^\d+$/.test(filters.renavam)) {
                yield put(vehiclesFailure())

                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messags.REQUEST.VEHICLES.REQUEST.RENAVAM_INVALID}`
                }))

                return
            }
        }

        /*
         * Validação do chassi
         */
        if (filters?.chassi) {
            if (!/^[A-Z0-9]+$/i.test(filters.chassi)) {
                yield put(vehiclesFailure())

                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messags.REQUEST.VEHICLES.REQUEST.CHASSI_INVALID}`
                }))

                return
            }
        }

        /*
         * Escolhe qual filtro será enviado para o backend
         */
        let serviceCall: any

        if (filters?.plate) {
            serviceCall = apply(
                vehicleService,
                vehicleService.getVehicles,
                [filters.plate, FIlterTypeVehicles.PLATE]
            )
        }
        else if (filters?.renavam) {
            serviceCall = apply(
                vehicleService,
                vehicleService.getVehicles,
                [filters.renavam, FIlterTypeVehicles.RENAVAM]
            )
        }
        else if (filters?.chassi) {
            serviceCall = apply(
                vehicleService,
                vehicleService.getVehicles,
                [filters.chassi, FIlterTypeVehicles.CHASSI]
            )
        }
        else {
            return
        }

        const { data: vehicles, cancelled } = yield race({
            data: serviceCall,
            cancelled: take(VehiclesTypes.VEHICLES_CANCEL)
        })

        if (cancelled) return

        yield put(vehiclesSuccess({
            data: vehicles
        }))

    } catch (err: any) {
        yield put(vehiclesFailure())

        /*
         * Erros retornados pelo backend
         */
        if (err?.response?.status >= 400 && err?.response?.status <= 499) {

            if (err.response.data.message === 'Required filter was not provided...') {
                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messags.REQUEST.VEHICLES.REQUEST.NULL_FILTERS}`
                }))
            }

            if (err.response.data.description === 'renavam must only have numeric digits!') {
                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messags.REQUEST.VEHICLES.REQUEST.RENAVAM_INVALID}`
                }))
            }
        }

        /*
         * Erro interno
         */
        if (err?.response?.status >= 500 && err?.response?.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messags.REQUEST.VEHICLES.REQUEST.INTERNAL}`
            }))
        }
    }
}


const vehiclesSaga = function* () {
    yield all([
        takeLatest(VehiclesTypes.VEHICLES_REQUEST, request),
    ])
}

export default vehiclesSaga
